import { memo, useCallback, useMemo } from 'react';
import { ManualArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const MANUAL_LOAD_HANDLING_TYPE_OPTIONS: {
  labelKey: string;
  value: ManualArea['loadHandlingType'];
}[] = [
  { labelKey: 'interface:properties.area.load_handling_type_options.intake', value: 'INTAKE' },
  { labelKey: 'interface:properties.area.load_handling_type_options.delivery', value: 'DELIVERY' },
  {
    labelKey: 'interface:properties.area.load_handling_type_options.intake_and_delivery',
    value: 'INTAKE_AND_DELIVERY',
  },
];

interface Props {
  selectedManualAreas: ManualArea[];
}

function ManualLoadHandlingTypeComponent({ selectedManualAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const options = useTranslatedOptions(MANUAL_LOAD_HANDLING_TYPE_OPTIONS);

  const loadHandlingType = useMemo(
    () => getCommonOption(selectedManualAreas.map((area) => area.loadHandlingType)),
    [selectedManualAreas],
  );

  const onChange = useCallback(
    (newValue: ManualArea['loadHandlingType']) => {
      fddmSvc.setPreference('manualArea.loadHandlingType', newValue);

      const updatedAreas = createEmptyAreas();
      selectedManualAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, { ...area, loadHandlingType: newValue });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [selectedManualAreas, fddmSvc],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.load_handling_type'>
      <SelectInput options={options} onChange={onChange} value={loadHandlingType} showMixed />
    </PropertiesLabel>
  );
}

export const LoadHandlingType = memo(ManualLoadHandlingTypeComponent);
